<script setup lang="ts">
import { ExclamationCircleIcon } from '@heroicons/vue/outline'

import { defineProps } from 'vue'
import { PersonalAccountPage } from '../../utilities/account/useAccountNavigationPersonal'
import { NavigationItemParent } from '../../typings'
import { LegalAccountPage } from '../../utilities/account/useAccountNavigationLegal'

const props = defineProps<{
    hasPageErrors: (page: PersonalAccountPage | LegalAccountPage) => boolean
    navigationItems: NavigationItemParent[]
    navigateTo: (page: PersonalAccountPage | LegalAccountPage, tab?: string) => void
    currentPage: PersonalAccountPage | LegalAccountPage
}>()

function isCurrentPage(page: PersonalAccountPage | LegalAccountPage) {
    return props.currentPage === page
}

const urlParams = new URLSearchParams(window.location.search)
const pageLink = urlParams.get('menu')
const tab = urlParams.get('tab')

if (pageLink) {
    props.navigateTo(pageLink, tab)
}
const emit = defineEmits(['navigate'])

function onNavigationClick(component) {
    emit('navigate')
    props.navigateTo(component)
}
</script>

<template>
    <template v-for="item in navigationItems" :key="item.name">
        <div v-if="!item.children">
            <button
                :class="[
                    isCurrentPage(item.component)
                        ? 'bg-gray-100 font-bold text-primary'
                        : 'bg-white font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                    'group flex w-full items-center rounded-md py-2 pl-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                ]"
                @click="onNavigationClick(item.component)">
                <component
                    :is="isCurrentPage(item.component) ? item.iconActive : item.icon"
                    :class="[
                        isCurrentPage(item.component) ? 'text-primary' : 'text-gray-400 group-hover:text-gray-500',
                        'mr-2 h-6 w-6 flex-shrink-0',
                    ]"
                    aria-hidden="true" />
                {{ item.name }}
            </button>
        </div>
        <div v-else class="space-y-1">
            <div
                class="group flex w-full items-center pl-2 pr-1 pt-4 text-left text-sm font-semibold uppercase text-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                <span class="flex-1">
                    {{ item.name }}
                </span>
            </div>
            <div class="space-y-1">
                <template v-for="subItem in item.children" :key="subItem.name">
                    <a
                        v-if="subItem.href && subItem.target === '_blank'"
                        :href="subItem.href"
                        target="_blank"
                        rel="noopener noreferrer"
                        :class="[
                            'bg-white font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                            'group flex w-full items-center rounded-md px-2 py-2 text-sm no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                        ]">
                        <div class="group flex w-full items-center text-left">
                            <component
                                :is="subItem.icon"
                                class="mr-2 h-6 w-6 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
                                aria-hidden="true" />
                            {{ subItem.name }}
                        </div>
                    </a>
                    <a
                        v-else-if="subItem.href"
                        :href="subItem.href"
                        :class="[
                            'bg-white font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                            'group flex w-full items-center rounded-md px-2 py-2 text-sm no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                        ]">
                        <div class="group flex w-full items-center text-left">
                            <component
                                :is="subItem.icon"
                                class="mr-2 h-6 w-6 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
                                aria-hidden="true" />
                            {{ subItem.name }}
                        </div>
                    </a>
                    <button
                        v-else
                        :class="[
                            isCurrentPage(subItem.component)
                                ? 'bg-gray-100 font-bold text-primary'
                                : 'bg-white font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                            'group flex w-full items-center rounded-md px-2 py-2 text-sm no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                        ]"
                        @click="onNavigationClick(subItem.component)">
                        <div class="group flex w-full items-center text-left">
                            <component
                                :is="isCurrentPage(subItem.component) ? subItem.iconActive : subItem.icon"
                                :class="[
                                    isCurrentPage(subItem.component)
                                        ? 'text-primary'
                                        : 'text-gray-400 group-hover:text-gray-500',
                                    'mr-2 h-6 w-6 flex-shrink-0',
                                ]"
                                aria-hidden="true" />
                            {{ subItem.name }}
                        </div>
                        <ExclamationCircleIcon
                            v-if="hasPageErrors(subItem.component)"
                            v-tooltip="$t('verification.complete_data_description_short')"
                            class="h-5 w-5 rounded-sm text-red-700 focus:outline-none focus:ring-2 focus:ring-primary"
                            aria-hidden="true" />
                    </button>
                </template>
            </div>
        </div>
    </template>
</template>

<style scoped></style>
